// client/src/components/pages/LexiStar/Billing/StatementDocument.tsx
//
// The usage statement as a document (ST-00207 stream A; m4b-contracts §2.3/§2.5): the same
// component on the Usage & billing page (screen) and on the print page the report engine
// renders to PDF (report definition usage_statement, /lexistar/billing/statement/:month
// ?print=1&run=…). It shows the server's lines exactly — the trucks and the three
// pass-through message lines — with subtotal, VAT and total, the plan the statement used, how
// the figures were worked out, and (on paper, or when opened on screen) the trucks behind
// the trucks line. It computes nothing.
//
// Two shapes of the trucks line (ST-00241): the flat monthly rate (every statement from
// 059 on, unless the plan was switched back) carries each truck's activation date, its share
// of the month (pro rata only in its first month), its amount and its written notice; the
// truck-days line (every statement finalised before, and the switch back) shows the
// truck-days ÷ days in the month with the days each truck reported. A final statement keeps
// the shape and the explanation it was finalised with.

import type { ReactNode } from 'react';
import { formatMiningDateTime } from '../../../../utils/dateUtils';
import { How, ReportDoc, Section } from '../Reports/reportAtoms';
import type { AssetDayRow, FlatMonthlyAssetsLine, StatementLine, UsageStatement } from '../../../../types/lexistar-billing';
import {
  BASIS_LABELS,
  basisOfSnapshot,
  chargeLabel,
  dateLabel,
  dayLabel,
  firstDay,
  flatExplanation,
  flatLineDetail,
  formatCount,
  formatUnits,
  formatVat,
  formatZar,
  isAssetsLine,
  isFlatAssetsLine,
  isMessagesLine,
  lastDay,
  monthLabel,
  monthOf,
  REMOVED_LABELS,
  removedWithoutNotice,
} from './billingLogic';

export interface StatementDocumentProps {
  statement: UsageStatement;
  print: boolean;
  /** The trucks behind the active-trucks line (on paper always; on screen when `showAssets`). */
  assets?: AssetDayRow[] | null;
  showAssets?: boolean;
  /** Screen only: actions under a line's description (the drill-downs). */
  lineActions?: (line: StatementLine) => ReactNode;
}

function statusChip(s: UsageStatement): ReactNode {
  if (s.preview) return <span className="lxr-chip lxr-chip--dashed">Preview — not stored</span>;
  if (s.status === 'final') return <span className="lxr-chip lxr-chip--ink">Final</span>;
  return <span className="lxr-chip lxr-chip--dashed">Draft</span>;
}

export function statusSentence(s: UsageStatement): string {
  const month = monthOf(s.period_month);
  if (s.preview) return `Preview of ${monthLabel(month)}: computed now from the current figures and not stored.`;
  if (s.status === 'final') {
    return `Final — finalised ${formatMiningDateTime(s.finalised_at)}${s.finalised_by ? ` by ${s.finalised_by}` : ''}. It does not change again.`;
  }
  return `Draft — rebuilt every night until ${monthLabel(month)} is finalised on the 1st of the next month.`;
}

export default function StatementDocument({ statement: s, print, assets, showAssets, lineActions }: StatementDocumentProps) {
  const month = monthOf(s.period_month);
  const assetsLine = s.lines.find(isAssetsLine) ?? null;
  const flat = assetsLine && isFlatAssetsLine(assetsLine) ? assetsLine : null;
  const truckDays = assetsLine && !isFlatAssetsLine(assetsLine) ? assetsLine : null;
  const messageLines = s.lines.filter(isMessagesLine);
  const billedMessages = messageLines.reduce((n, l) => n + l.billable_units, 0);
  const plan = s.plan_snapshot;
  const basis = flat ? 'flat_monthly' : basisOfSnapshot(plan);
  const trucks = print || showAssets ? assets ?? null : null;
  const showFlatTrucks = !!flat && (print || !!showAssets);
  const unnoticed = flat ? removedWithoutNotice(flat) : [];

  return (
    <ReportDoc
      print={print}
      label={`Usage statement ${monthLabel(month)}`}
      reportName="Usage statement"
      title={`Usage statement · ${monthLabel(month)}`}
      subtitle={s.tenant_name || undefined}
      chips={statusChip(s)}
      watermark={s.status === 'final' ? null : s.preview ? 'PREVIEW' : 'DRAFT'}
      generatedAt={s.generated_at}
      facts={[
        { label: 'Period', value: `${dayLabel(firstDay(month))} – ${dayLabel(lastDay(month))} ${month.slice(0, 4)}` },
        { label: 'Status', value: s.preview ? 'Preview' : s.status === 'final' ? 'Final' : 'Draft' },
        {
          label: s.status === 'final' ? 'Finalised' : 'Figures as of',
          value: `${formatMiningDateTime(s.status === 'final' ? s.finalised_at : s.generated_at)} SAST`,
        },
        {
          label: 'Plan',
          wide: true,
          value:
            basis === 'flat_monthly'
              ? `${plan?.plan_code || 'standard'} · ${BASIS_LABELS.flat_monthly}: ${formatZar(plan?.asset_rate_zar ?? 0)} per truck per month · ${formatCount(plan?.included_messages ?? 0)} messages included`
              : `${plan?.plan_code || 'standard'} · ${formatZar(plan?.asset_rate_zar ?? 0)} per active truck per month · ${formatCount(plan?.included_messages ?? 0)} messages included`,
        },
        { label: 'VAT', value: formatVat(plan?.vat_rate) },
      ]}
      footer={
        <>
          <span>
            {s.id ? `Statement ${s.id.slice(0, 8)} · ` : ''}
            {basis === 'flat_monthly'
              ? 'trucks at a flat monthly rate from their activation until a written notice'
              : 'active trucks from valid GPS fixes per SAST day'}{' '}
            · messages from the messaging cost log, at cost
          </span>
          <span>{s.preview ? 'Preview — not stored' : s.status === 'final' ? 'Final' : 'Draft — changes until finalised'}</span>
        </>
      }
    >
      <dl className="lxr-kpis" aria-label="Statement totals">
        {flat ? (
          <div className="lxr-kpi">
            <dt>Trucks billed</dt>
            <dd>
              {formatUnits(flat.units)}
              <small>
                {formatCount(flat.full_months)} full month · {formatCount(flat.first_months)} first month
              </small>
            </dd>
          </div>
        ) : (
          <div className="lxr-kpi">
            <dt>Active trucks</dt>
            <dd>
              {formatUnits(truckDays?.units ?? 0)}
              <small>{formatCount(truckDays?.asset_days ?? 0)} truck-days</small>
            </dd>
          </div>
        )}
        <div className="lxr-kpi">
          <dt>Messages billed</dt>
          <dd>
            {formatCount(billedMessages)}
            <small>after {formatCount(plan?.included_messages ?? 0)} included</small>
          </dd>
        </div>
        <div className="lxr-kpi">
          <dt>Subtotal</dt>
          <dd>{formatZar(s.subtotal_zar)}</dd>
        </div>
        <div className="lxr-kpi">
          <dt>VAT</dt>
          <dd>{formatZar(s.vat_zar)}</dd>
        </div>
        <div className="lxr-kpi">
          <dt>Total</dt>
          <dd data-testid="statement-total">{formatZar(s.total_zar)}</dd>
        </div>
      </dl>

      <div className="lxr-stack">
        <Section title="Charges" aside={`${s.currency} · ${monthLabel(month)}`}>
          <div className="lxr-scroll">
            <table className="lxr-table" aria-label="Statement lines">
              <thead>
                <tr>
                  <th scope="col">Description</th>
                  <th scope="col" className="lxr-num">
                    Quantity
                  </th>
                  <th scope="col" className="lxr-num">
                    Included
                  </th>
                  <th scope="col" className="lxr-num">
                    Billable
                  </th>
                  <th scope="col" className="lxr-num">
                    Rate
                  </th>
                  <th scope="col" className="lxr-num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.lines.map((l) =>
                  isAssetsLine(l) ? (
                    <tr key="assets" data-testid="line-assets">
                      <td className="lxr-strong">
                        {l.description}
                        <small>
                          {isFlatAssetsLine(l)
                            ? flatLineDetail(l)
                            : `${formatCount(l.asset_days)} truck-days ÷ ${l.days_in_month} days`}
                        </small>
                        {!print && lineActions?.(l)}
                      </td>
                      <td className="lxr-num">{formatUnits(l.units)}</td>
                      <td className="lxr-num lxr-muted">—</td>
                      <td className="lxr-num">{formatUnits(l.units)}</td>
                      <td className="lxr-num">{formatZar(l.rate_zar)}</td>
                      <td className="lxr-num lxr-strong">{formatZar(l.amount_zar)}</td>
                    </tr>
                  ) : (
                    <tr key={l.channel} data-testid={`line-${l.channel}`}>
                      <td className="lxr-strong">
                        {l.description}
                        <small>passed through at cost</small>
                        {!print && lineActions?.(l)}
                      </td>
                      <td className="lxr-num">{formatCount(l.units)}</td>
                      <td className="lxr-num">{formatCount(l.included)}</td>
                      <td className="lxr-num">{formatCount(l.billable_units)}</td>
                      <td className="lxr-num lxr-muted">at cost</td>
                      <td className="lxr-num lxr-strong">{formatZar(l.amount_zar)}</td>
                    </tr>
                  )
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>Subtotal</td>
                  <td className="lxr-num">{formatZar(s.subtotal_zar)}</td>
                </tr>
                <tr>
                  <td colSpan={5}>VAT {formatVat(plan?.vat_rate)}</td>
                  <td className="lxr-num">{formatZar(s.vat_zar)}</td>
                </tr>
                <tr>
                  <td colSpan={5}>Total ({s.currency})</td>
                  <td className="lxr-num">{formatZar(s.total_zar)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <How>
            {flat &&
              flatExplanation(flat).map((sentence, i) => (
                <div key={i} data-testid="how-flat">
                  {sentence}
                </div>
              ))}
            {truckDays && (
              <div>
                active trucks = truck-days with a valid GPS fix ÷ days in the month = {formatCount(truckDays.asset_days)} ÷{' '}
                {truckDays.days_in_month} = {formatUnits(truckDays.units)} (two decimals, half up); {formatUnits(truckDays.units)} ×{' '}
                {formatZar(truckDays.rate_zar)} = {formatZar(truckDays.amount_zar)}
              </div>
            )}
            {!flat && (
              <div>
                a truck counts for a day (SAST) when it sent at least one valid GPS fix that day; each truck is billed for the days it reported
              </div>
            )}
            <div>
              messages = the month’s entries in the messaging cost log, passed through at cost; the {formatCount(plan?.included_messages ?? 0)}{' '}
              included messages are credited against WhatsApp first, then SMS, then email — the earliest messages of the month first, whole
              messages only
            </div>
            <div>
              VAT = {formatZar(s.subtotal_zar)} × {formatVat(plan?.vat_rate)} = {formatZar(s.vat_zar)} (to the cent, half up); total ={' '}
              {formatZar(s.subtotal_zar)} + {formatZar(s.vat_zar)} = {formatZar(s.total_zar)}
            </div>
          </How>
        </Section>

        {unnoticed.length > 0 && (
          <div className="lxr-banner lxr-banner--alert" role="note" data-testid="removed-without-notice">
            <span>
              <span className="lxr-alert">
                {unnoticed.length === 1 ? '1 truck is' : `${formatCount(unnoticed.length)} trucks are`} still billed although{' '}
                {unnoticed.length === 1 ? 'it was' : 'they were'} removed in the app or {unnoticed.length === 1 ? 'its' : 'their'} GPS device
                was unlinked, and no written notice was recorded:
              </span>{' '}
              {unnoticed.map((a) => a.registration || a.asset_id.slice(0, 8)).join(', ')}. A truck leaves the subscription only with written
              notice: billing ends one calendar month after the notice is received.
            </span>
          </div>
        )}

        {flat && showFlatTrucks && <FlatTrucks line={flat} month={month} />}

        {!flat && trucks && (
          <Section title="Active trucks" aside="the days each truck sent a valid GPS fix (SAST)">
            {trucks.length === 0 ? (
              <p className="lxr-empty">No truck reported a valid GPS fix in {monthLabel(month)}.</p>
            ) : (
              <div className="lxr-scroll">
                <table className="lxr-table" aria-label="Active trucks">
                  <thead>
                    <tr>
                      <th scope="col">Truck</th>
                      <th scope="col" className="lxr-num">
                        Days
                      </th>
                      <th scope="col">First day</th>
                      <th scope="col">Last day</th>
                      <th scope="col">Days with a valid fix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trucks.map((t) => (
                      <tr key={t.asset_id}>
                        <td className="lxr-strong">
                          {t.registration || t.asset_id.slice(0, 8)}
                          {t.fleet_no && <small>fleet no. {t.fleet_no}</small>}
                        </td>
                        <td className="lxr-num">{formatCount(t.days)}</td>
                        <td>{dayLabel(t.first_day)}</td>
                        <td>{dayLabel(t.last_day)}</td>
                        <td>{t.dates.map((d) => d.slice(8, 10).replace(/^0/, '')).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Truck-days</td>
                      <td className="lxr-num">{formatCount(trucks.reduce((n, t) => n + t.days, 0))}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Section>
        )}
      </div>
    </ReportDoc>
  );
}

/** The trucks of a flat monthly statement: activation, what was charged, the share, the amount, the notice. */
function FlatTrucks({ line, month }: { line: FlatMonthlyAssetsLine; month: string }) {
  return (
    <Section title="Truck charges" aside={`each truck on the subscription in ${monthLabel(month)}`}>
      {line.assets.length === 0 ? (
        <p className="lxr-empty">No truck was on the subscription in {monthLabel(month)}.</p>
      ) : (
        <div className="lxr-scroll">
          <table className="lxr-table" aria-label="Truck charges">
            <thead>
              <tr>
                <th scope="col">Truck</th>
                <th scope="col">Active since</th>
                <th scope="col">Charged</th>
                <th scope="col" className="lxr-num">
                  Share of the month
                </th>
                <th scope="col" className="lxr-num">
                  Amount
                </th>
                <th scope="col">Written notice</th>
              </tr>
            </thead>
            <tbody>
              {line.assets.map((a) => (
                <tr key={a.asset_id} data-testid={`truck-${a.asset_id}`}>
                  <td className="lxr-strong">
                    {a.registration || a.asset_id.slice(0, 8)}
                    {a.fleet_no && <small>fleet no. {a.fleet_no}</small>}
                    {a.removed && !a.notice_id && (
                      <small>
                        <span className="lxr-tag lxr-tag--attention">{REMOVED_LABELS[a.removed]}</span>
                      </small>
                    )}
                  </td>
                  <td>{dateLabel(a.activated_on)}</td>
                  <td>{chargeLabel(a)}</td>
                  <td className="lxr-num">{formatUnits(a.fraction)}</td>
                  <td className="lxr-num lxr-strong">{formatZar(a.amount_zar)}</td>
                  <td>
                    {a.notice_received_on ? (
                      <>
                        received {dateLabel(a.notice_received_on)}
                        <small>billing ends {dateLabel(a.billing_ends_on)}</small>
                      </>
                    ) : (
                      <span className="lxr-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>Trucks billed</td>
                <td className="lxr-num">{formatUnits(line.units)}</td>
                <td className="lxr-num">{formatZar(line.amount_zar)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Section>
  );
}
